import _ from "lodash";
import { DateTime } from "luxon";
import { action, makeObservable, when } from "mobx";
import { inject, observer } from "mobx-react";
import React, { Fragment } from "react";
import Draggable from "react-draggable";
import Select from "react-select";
import tinycolor from "tinycolor2";

import resizeObserver from "shared/resizeObserver";
import config from "../../config";
import {
  formatDateTime,
  formatTime,
  toLuxon,
} from "../../shared/dateTimeUtils";
import Icon from "../../shared/Icon";
import Toggle from "../../shared/Toggle/Toggle";
import ZoomControl from "../../shared/ZoomControl";
import "./Monitor.scss";
import MonitorSuper from "./MonitorSuper";
import {
  getLeftMarkerDeltaByTimestamp,
  getMarkerDeltaByCanvasWidth,
  getMinMarkerDeltaByPosition,
  getRightMarkerDeltaByTimestamp,
  getChartHeight,
} from "./MonitorUtils";
import SnippetWaveAnalyser from "./SnippetWaveAnalyser";
import SubNav, { DisableableLink } from "./SubNav";
import formatNumber from "../../shared/formatNumber";
import { generateYScale } from "./MonitorUtils";
import Swal from "../../shared/Swal";
import { MRN_STATE } from "../../shared/Patient/MrnState";
import { reactSelectTheme } from "../../shared/colorScheme";
import className from "../../shared/className";

import refresh from "./Tools/Tools.refesh";

const rhythmStripHeight = 120;

class Tools extends MonitorSuper {
  constructor(props) {
    super(props);

    makeObservable(this, {
      toggleMarchOut: action,
      togglePaperMode: action,
      toggleMeasureMode: action,
      calcDistances: action,
      handleDragBoundary: action,
      secondsChange: action,
      selectTime: action,
      updateTime: action,
    });

    this.store.logSnippetCreation({ stage: "started" });

    this.store.liveMode = false;
    this.store.fetchedTimeRange = undefined;
    this.store.yOffset = 0;
    this.store.pixelsPerSecond = 400;
    this.store.marchout = false;
    this.intervalPlaceholder = this.props.store.serverPrefs?.pm
      ?.snippetsShowMutedMeasurementTextAsDashes
      ? "--"
      : "0";

    if (this.store.chartEndTime) {
      this.store.originalChartEndTime = this.store.chartEndTime;
    } else {
      this.store.chartEndTime = DateTime.now();
    }
    this.store.userModifiedMeasurements = false;

    this.setChartSize = this.setChartSize.bind(this);
    this.renderCharts = this.frameDebounce(this.renderCharts.bind(this)).bind(
      this,
    );
    this.toggleMeasureMode = this.toggleMeasureMode.bind(this);
    this.toggleCalipersMode = this.toggleCalipersMode.bind(this);
    this.createStatement = this.createStatement.bind(this);
    this.handleOverDrag = handleOverDrag.bind(this);
    this.secondsChange = this.secondsChange.bind(this);
    this.handleDragBoundary = this.frameDebounce(
      this.handleDragBoundary.bind(this),
    ).bind(this);
    this.startDragBothCalipers = this.startDragBothCalipers.bind(this);
    this.toggleMarchOut = this.toggleMarchOut.bind(this);
    this.selectTime = this.selectTime.bind(this);
    this.updateTime = this.updateTime.bind(this);
    this.togglePaperMode = this.togglePaperMode.bind(this);
    this.toggleExpandControls = this.toggleExpandControls.bind(this);
    this.refresh = refresh.bind(this);
    this.isSnippetRoute =
      this.props.location.pathname.includes("/monitor/snippet/");
  }

  componentDidMount() {
    super.componentDidMount();

    // Set the zoom so that both waveforms fit. (or just one if the window is 600px tall or less)
    const chartHeight = this.store.pixelsPerSecond / this.store.chartAspect;
    const maxChartHeight = this.refChart.clientHeight;
    if (chartHeight > maxChartHeight) {
      this.store.zoom = this.store.zoom * (maxChartHeight / chartHeight);
    }

    this.setChartSize({ onMount: true }); // this will call renderCharts();
    requestAnimationFrame(() => {
      this.refChart && resizeObserver.observe(this.refChart, this.setChartSize);
    });
    clearInterval(this.updateStripInterval);
    this.updateStripInterval = setInterval(
      this.updateStripBuffer.bind(this),
      1000,
    );
    this.updateStripBuffer();
    when(
      () => this.store.chartEndTime,
      () => {
        this.defaultSeconds();
      },
    );
    this.defaultLeads();
  }

  componentDidUpdate() {
    super.componentDidUpdate();
    this.renderCharts();
  }

  componentWillUnmount() {
    resizeObserver.disconnect();
    super.componentWillUnmount();
    this.handleOverDrag = _.noop;
    this.defaultSeconds = _.noop;
    clearInterval(this.updateStripInterval);
  }
  /* istanbul ignore next */
  setZoom(initialZoom) {
    this.store.pixelsPerSecond *= initialZoom;
    this.store.chartHeight *= initialZoom;
  }

  get lengthOptions() {
    let lengthOptions =
      _.get(
        this,
        "props.store.prefs.userPreferences.server.pm.snippetsDurations",
        [],
      ) || [];
    return lengthOptions.map((value) => ({
      value,
      label: `${_.round(value, 1)} seconds`,
    }));
  }
  get snippetsEnabled() {
    return Boolean(this.lengthOptions.length);
  }

  render() {
    const patient = this.props.store.patient;
    const stripWidth = (this.store.stripWidth = _.get(
      this.refs,
      "rhythmStrip.clientWidth",
      window.innerWidth - 28,
    ));
    const chartHeight = this.store.pixelsPerSecond / this.store.chartAspect;
    const none = { value: "none", label: "none" };

    let hr = this.store.getHeartRate();
    let heartRate;

    if (hr === undefined) {
      heartRate = this.store.discretes.find((d) => d.vitalType === "HR");
      heartRate = heartRate?.values[0];
    } else if (hr) {
      heartRate = hr.value;
    }

    let waveform1options, waveform2options, wave1, wave2;
    if (this.store.config) {
      waveform1options = this.store.config.waveGroups
        .filter((group) => group.key !== this.store.selectedWaveform2key)
        .map((group) => ({ value: group.key, label: group.title }));
      waveform2options = this.store.config.waveGroups
        .filter((group) => group.key !== this.store.selectedWaveform1key)
        .map((group) => ({ value: group.key, label: group.title }));
      wave1 = waveform1options.find(
        (w) => w.value === this.store.selectedWaveform1key,
      );
      wave2 = waveform2options.find(
        (w) => w.value === this.store.selectedWaveform2key,
      );
    }

    return (
      <div
        {...className("monitor snippets widget", {
          compact: config.pm.hideSideBar,
        })}
      >
        <SubNav
          asmpid={this.asmpid}
          location={this.props.location}
          hideMainNav={this.props.store.hideMainNav}
        >
          <span>
            <DisableableLink
              disabled={!this.store.enableSelectTime}
              to={this.selectTimePath()}
              onClick={this.selectTime}
            >
              Select Time
            </DisableableLink>
            <DisableableLink
              disabled={this.props.store.hideMainNav}
              to={`/patient/${this.asmpid}/monitor/live`}
            >
              Live
            </DisableableLink>
          </span>
        </SubNav>
        <div
          className={this.cn({
            "monitor-body": true,
            "paper-mode": this.paperMode,
          })}
        >
          <div
            {...this.className({
              controls: 1,
              expanded: this.store.controlsExpanded,
            })}
          >
            <Icon symbol="gear" onClick={this.toggleExpandControls} />
            {this.store.config ? (
              <Fragment>
                <Select
                  name="waveform1"
                  key="1"
                  className="Select"
                  classNamePrefix="Select"
                  onChange={(option) =>
                    (this.store.selectedWaveform1key = option?.value)
                  }
                  value={waveform1options.find(
                    (w) => w.value === this.store.selectedWaveform1key,
                  )}
                  options={this.store.config.waveGroups
                    .filter(
                      (group) => group.key !== this.store.selectedWaveform2key,
                    )
                    .map((group) => ({ value: group.key, label: group.title }))}
                  isSearchable={false}
                  theme={reactSelectTheme({
                    darkMode: this.props.store.darkMode,
                  })}
                />
                <Select
                  name="waveform2"
                  key="2"
                  className="Select"
                  classNamePrefix="Select"
                  onChange={(option) =>
                    (this.store.selectedWaveform2key = option?.value)
                  }
                  value={
                    waveform2options.find(
                      (w) => w.value === this.store.selectedWaveform2key,
                    ) || none
                  }
                  options={[
                    none,
                    ...this.store.config.waveGroups
                      .filter(
                        (group) =>
                          group.key !== this.store.selectedWaveform1key,
                      )
                      .map((group) => ({
                        value: group.key,
                        label: group.title,
                      })),
                  ]}
                  isSearchable={false}
                  theme={reactSelectTheme({
                    darkMode: this.props.store.darkMode,
                  })}
                />
              </Fragment>
            ) : null}
            {this.lengthOptions.length ? (
              <Select
                name="seconds"
                className="Select"
                classNamePrefix="Select"
                onChange={this.secondsChange}
                value={this.store.seconds}
                options={this.lengthOptions}
                theme={reactSelectTheme({
                  darkMode: this.props.store.darkMode,
                })}
              />
            ) : null}
            <div className="marker-controls">
              <div
                className="mobile-indicators"
                onClick={this.toggleExpandControls}
              >
                <span className="indicator">{_.get(wave1, "label")}</span>
                <span className="indicator">{_.get(wave2, "label")}</span>
                <span className="indicator">
                  {_.get(this.store.seconds, "label")}
                </span>
              </div>
              <div className="button-group">
                <button
                  onClick={this.toggleMeasureMode}
                  className={this.cn({ selected: this.store.measurementMode })}
                >
                  Measure
                </button>
                <button
                  onClick={this.toggleCalipersMode}
                  className={this.cn({ selected: this.store.caliperMode })}
                >
                  Calipers
                </button>
              </div>
              <Toggle
                onChange={this.toggleMarchOut}
                disabled={!this.store.caliperMode}
                label="Marchout"
              />
              <Toggle
                onChange={this.togglePaperMode}
                checked={!!this.paperMode}
                label="Paper Mode"
              />
            </div>
            {_.get(
              this.props,
              "store.prefs.userPreferences.server.pm.snippetsEnabled",
            ) ? (
              <button
                className="create-statement"
                onClick={this.createStatement}
                disabled={
                  patient?.mrnState === MRN_STATE.FIXED ||
                  this.store.caliperMode
                    ? "disabled"
                    : null
                }
                title={
                  this.store.caliperMode
                    ? "Snippet creation is disabled in Caliper mode"
                    : null
                }
              >
                Create Snippet
              </button>
            ) : null}
          </div>
          <div className="chart-header">
            <div className="heart-rate">HR: {heartRate}</div>
            {this.store.measurementMode ? (
              <div className="calc-distances">
                <div>
                  <span className="label">PR:</span>{" "}
                  {formatNumber(this.store.PR, this.intervalPlaceholder)}
                </div>
                <div>
                  <span className="label">QRS:</span>{" "}
                  {formatNumber(this.store.QRS, this.intervalPlaceholder)}
                </div>
                <div>
                  <span className="label">QT:</span>{" "}
                  {formatNumber(this.store.QT, this.intervalPlaceholder)}
                </div>
                {config.pm.qtc ? (
                  <div>
                    <span className="label">QTc:</span>{" "}
                    {formatNumber(this.store.QTc)}
                  </div>
                ) : null}
              </div>
            ) : null}
            <div
              className="timestamp"
              ref={(r) => (this.refTimestamp = r)}
            ></div>
          </div>
          <div className="chart" ref={(r) => (this.refChart = r)}>
            <canvas
              ref={(r) => (this.refCanvas = r)}
              onMouseDown={this.canvasMouseDown}
              onTouchStart={this.canvasMouseDown}
              data-testid="canvas"
            />
            {this.store.config && this.store.measurements
              ? this.store.toolsWaveforms.map((group, i) => {
                  if (!group) {
                    return undefined;
                  }
                  return (
                    <div
                      className={this.cn({
                        "monitor-chart": 1,
                      })}
                      id={`chart-${group.key}`}
                      key={i}
                    >
                      <div
                        className="label"
                        style={{
                          top:
                            chartHeight * i +
                            chartHeight / 2 -
                            this.store.offsetY +
                            "px",
                        }}
                      >
                        {group.title}
                      </div>
                    </div>
                  );
                })
              : null}
            <ZoomControl
              input={{ "data-testid": "zoom" }}
              zoom={this.store.zoom}
              zoomChange={this.zoomChange}
            />

            {this.store.caliperMode ? (
              <div
                className="caliper-distance"
                style={{
                  top: this.store.markers[0].position.y,

                  left: this.freeCalipers
                    ? getMinMarkerDeltaByPosition(
                        this.store.markers[0].position.x,
                        this.store.markers[1].position.x,
                      ) + "px"
                    : getLeftMarkerDeltaByTimestamp(
                        stripWidth,
                        this.store.markers[0].timestamp,
                        this.store.markers[1].timestamp,
                        this.store.chartEndTime,
                        this.store.pixelsPerSecond,
                      ) + "px",
                  right: this.freeCalipers
                    ? getMarkerDeltaByCanvasWidth(
                        this.refCanvas.width,
                        this.store.markers[0].position.x,
                        this.store.markers[1].position.x,
                      )
                    : getRightMarkerDeltaByTimestamp(
                        stripWidth,
                        this.store.markers[0].timestamp,
                        this.store.markers[1].timestamp,
                        this.store.chartEndTime,
                        this.store.pixelsPerSecond,
                      ) + "px",
                }}
              >
                <div
                  onMouseDown={this.startDragBothCalipers}
                  onTouchStart={this.startDragBothCalipers}
                >
                  {this.store.caliperDistance}
                </div>
              </div>
            ) : null}
            {this.markers}
          </div>
          <div className="rhythm-strip" ref={(r) => (this.refRhythmStrip = r)}>
            <canvas
              ref={(r) => (this.refRhythmStripCanvas = r)}
              onMouseDown={(e) =>
                this.canvasMouseDown({ ...e, rhythmStrip: true })
              }
              onTouchStart={(e) =>
                this.canvasMouseDown({ ...e, rhythmStrip: true })
              }
            />
            {this.store.boundaries ? this.boundaries(stripWidth) : null}
            <div
              className="timestamp"
              ref={(r) => (this.refStripTimestamp = r)}
            ></div>
            {this.store.measurementMode
              ? this.store.markers.map((caliper, i) => {
                  return (
                    <div
                      className="caliper"
                      key={i}
                      style={{
                        height: rhythmStripHeight,
                        left: this.timeToPosition(caliper.timestamp, true),
                      }}
                    >
                      <div className="handle">{caliper.label}</div>
                    </div>
                  );
                })
              : null}
          </div>
        </div>
      </div>
    );
  }

  positionToTime(x, isStrip) {
    const pixelsPerSecond = isStrip
      ? this.store.minPixelsPerSecond
      : this.store.pixelsPerSecond;
    return this.startTime(
      isStrip ? this.refRhythmStripCanvas : this.refCanvas,
    ).plus({
      seconds: x / pixelsPerSecond,
    });
  }

  get freeCalipers() {
    return this.store.caliperMode && !this.zooming;
  }
  get markers() {
    return this.store.markers.map((marker, i) => {
      let x;
      if (this.freeCalipers) {
        x = marker.position.x;
      } else {
        x = this.timeToPosition(marker.timestamp);
      }

      let opacity = marker.muted ? 0.5 : 1;
      if (x < 0 || x > this.store.chartWidth) {
        return null;
      }
      return [
        <Draggable
          key={i}
          defaultPosition={marker.position}
          onStart={this.startDragMarker}
          onDrag={(e, data) => this.handleDragMarker(e, data, i)}
          onStop={(e, data) => this.stopDragMarker(e, data, i)}
          ref={`caliper-${i}`}
          position={{
            x,
            y: marker.position.y,
          }}
        >
          <div
            {...this.className({
              caliper: true,
              muted: marker.muted,
            })}
            style={{ opacity }}
          >
            <div className="handle" ref={(r) => (this.refHandle = r)}>
              {marker.label}
            </div>
          </div>
        </Draggable>,
        <div
          className="marker-line"
          style={{
            transform: `translate(${x}px, 0)`,
            height: `${window.innerHeight}px`,
            opacity,
          }}
          key={`line${i}`}
        ></div>,
      ];
    });
  }

  toggleMarchOut() {
    this.store.marchout = !this.store.marchout;
    if (this.store.caliperMode) {
      this.store.markers.forEach((marker) => {
        if (this.store.marchout) {
          marker.timestamp = this.positionToTime(marker.position.x).toISO();
        } else {
          marker.position.x = this.timeToPosition(marker.timestamp);
        }
      });
    }
    this.renderCharts();
  }
  syncCaliperPositions() {
    if (this.store.caliperMode) {
      this.store.markers.forEach((marker) => {
        marker.position.x = this.timeToPosition(marker.timestamp);
      });
    }
  }
  toggleExpandControls() {
    this.store.controlsExpanded = !this.store.controlsExpanded;
  }

  togglePaperMode(e) {
    this.paperMode = e.target.checked;
  }
  get paperMode() {
    return this.props.store.prefs?.userPreferences?.client?.pm?.paperMode;
  }
  set paperMode(mode) {
    this.props.store
      .setPref("pm.paperMode", mode)
      .then(() => this.renderCharts());
  }
  get chartLengthSeconds() {
    return this.refCanvas?.width / this.store.pixelsPerSecond;
  }

  toggleMeasureMode() {
    if (this.store.measurementMode) {
      this.store.clearMarkers();
    } else {
      if (!this.store.chartEndTime) return;
      let markers;
      if (this.store.caliperMode) {
        const savedMarkers = this.store.markers;
        if (_.get(this.store.savedMarkers, "length") === 4) {
          markers = this.store.savedMarkers;
        }
        this.store.savedMarkers = savedMarkers;
      }

      if (!markers) {
        // Re-center boundaries.
        this.secondsChange(this.store.seconds);
        // get start and end time.
        this.store.chartWidth = this.refCanvas.width;
        const chartLengthSeconds = this.chartLengthSeconds;
        const startTime = this.store.chartEndTime.minus({
          seconds: chartLengthSeconds,
        });
        let y = this.refCanvas.height / 6;
        const P_TIMESTAMP = startTime.plus({
          seconds: chartLengthSeconds / 2 - 0.4,
        });
        const QR_TIMESTAMP = P_TIMESTAMP.plus({ seconds: 0.2 });
        const S_TIMESTAMP = QR_TIMESTAMP.plus({ seconds: 0.2 });
        const T_TIMESTAMP = S_TIMESTAMP.plus({ seconds: 0.2 });

        const drawMarkers = [
          {
            label: "P",
            timestamp: P_TIMESTAMP,
          },
          {
            label: "Q/R",
            timestamp: QR_TIMESTAMP,
          },
          {
            label: "S",
            timestamp: S_TIMESTAMP,
          },
          {
            label: "T",
            timestamp: T_TIMESTAMP,
          },
        ];
        const addPosToMarkers = drawMarkers.map((caliper, i) => {
          if (this.store.savedMeasurements) {
            caliper.timestamp = this.store.savedMeasurements[i];
          }
          return {
            ...caliper,
            position: { x: 40 * (i + 1) + this.store.chartWidth * 0.4, y },
          };
        });
        markers = addPosToMarkers;
        this.findPQRST(addPosToMarkers);
      }

      this.store.markers = markers;
      this.calcDistances();
    }
  }
  toggleCalipersMode() {
    if (this.store.caliperMode) {
      this.store.clearMarkers();
    } else {
      let markers;

      if (this.store.measurementMode) {
        const savedMarkers = this.store.markers;
        if (_.get(this.store.savedMarkers, "length") === 2) {
          markers = this.store.savedMarkers;
        }
        this.store.savedMarkers = savedMarkers;
      }

      if (!markers) {
        this.store.chartWidth = this.refCanvas.width;

        let y = this.refCanvas.height / 2;
        const start = this.centerTime.plus({ seconds: 0.25 });
        const end = this.centerTime.minus({ seconds: 0.25 });
        markers = [
          {
            label: "⇤",
            position: { x: this.timeToPosition(start), y },
            timestamp: start.toISO(),
          },
          {
            label: "⇥",
            position: { x: this.timeToPosition(end), y },
            timestamp: end.toISO(),
          },
        ];
        this.findPeaks(markers);
      }

      this.store.markers = markers;
      this.calcDistances();
    }
  }
  findPeaks(markers) {
    const centerSegment = this.store.measurements?.data?.find(
      (segment) => segment.unixTimestamp >= this.centerTime,
    );
    if (!centerSegment) {
      return;
    }

    const selectedKey = this.store.selectedWaveform1key;
    const wave = centerSegment.waveData.find(
      (wave) => wave.key === selectedKey,
    );
    const waveData = _.get(wave, "data", []);
    const analyzer = new SnippetWaveAnalyser(waveData, waveData.length);
    if (analyzer.mPeakIndexes.length === 1) {
      // If there's only one peak, add another one a half second later.
      analyzer.mPeakIndexes.push(
        analyzer.mPeakIndexes[0] + waveData.length / 2,
      );
    }
    markers.forEach((marker, i) => {
      let peakIndex = analyzer.mPeakIndexes[i];
      if (peakIndex) {
        marker.timestamp = toLuxon(centerSegment.timestamp).plus({
          seconds: peakIndex / waveData.length,
        });
        marker.position.x = this.timeToPosition(marker.timestamp);
      }
    });
  }
  findPQRST(markers) {
    const centerTime = this.centerTime;
    const centerSegments =
      this.store.measurements?.data?.filter((segment) => {
        return (
          segment.dateTime >= centerTime.minus({ seconds: 2 }) &&
          segment.dateTime <= centerTime.plus({ seconds: 2 })
        );
      }) || [];

    const selectedKey = this.store.selectedWaveform1key;
    const waves = centerSegments
      .map((segment) =>
        segment.waveData.find((wave) => wave.key === selectedKey),
      )
      .map((wave) => _.get(wave, "data", []).slice());
    if (!waves.length) {
      return console.error("no wave data to analyze");
    }
    const waveGroup = this.store.config.waveGroups.find(
      (group) => group.key === selectedKey,
    );

    if (!waves.length) {
      return;
    }
    const waveData = _.flatten(waves);
    const sampleRate = waves[0].length;

    function isNull(x) {
      return !x || x <= 0 || x >= waveData.length - 1;
    }

    let markersPromise;
    if (config.pm.qrsProcessor) {
      markersPromise = this.store
        .fetchQRS({
          query: {
            qrs: Math.round(
              this.centerTime.diff(centerSegments[0].dateTime, "seconds")
                .seconds * sampleRate,
            ),
          },
          model: {
            name: waveGroup.title,
            sampleRate,
            waveId: waveGroup.key,
            ecgWave: waveData,
          },
        })
        .then((res) => {
          const fullestSet = _.maxBy(res.qrsIndexes, (indexObj) =>
            Object.values(indexObj).filter((index) => !isNull(index)),
          );

          const pqrstIndexes = [
            fullestSet.p,
            fullestSet.q,
            fullestSet.r,
            fullestSet.s,
            fullestSet.t,
          ];

          [fullestSet.p, fullestSet.q, fullestSet.s, fullestSet.t].forEach(
            (val) => {
              markers.muted = isNull(val);
            },
          );
          return pqrstIndexes;
        });
    } else {
      const analyzer = new SnippetWaveAnalyser(waveData, sampleRate);
      const snippetPQRST = analyzer.findIndexes(
        analyzer.mPeakIndexes[parseInt(analyzer.mPeakIndexes.length / 2)],
      );
      let { pIndex, qIndex, rIndex, sIndex, tIndex } = snippetPQRST;

      if (isNull(rIndex)) {
        return Promise.reject("no R index");
      }
      const pqrstIndexes = [pIndex, qIndex, rIndex, sIndex, tIndex];

      if (config.pm.snippetRawData) {
        this.store.snippetRawData = {
          defaultPQRSTIndex: _.clone(pqrstIndexes),
          sampleRate,
          waveData,
          waveLengthSeconds: centerSegments.length,
          waveStartTime: centerSegments[0].dateTime,
          waveEndTime: _.last(centerSegments).dateTime.plus({ seconds: 1 }),
        };
      }

      markersPromise = Promise.resolve(pqrstIndexes);
    }

    markersPromise
      .then((pqrstIndexes) => {
        const foundIndexes = pqrstIndexes.filter((n) => !isNull(n));
        // check if they're in order
        if (
          !foundIndexes.every((item, i, arr) =>
            i > 0 ? item > arr[i - 1] : true,
          )
        ) {
          console.error("PQRST indexes out of order.");
          return;
        }

        if (config.pm.snippetRawData) {
          this.store.snippetRawData = {
            defaultPQRSTIndex: _.clone(pqrstIndexes),
            sampleRate,
            waveData,
            waveLengthSeconds: centerSegments.length,
            waveStartTime: toLuxon(centerSegments[0].timestamp),
            waveEndTime: toLuxon(_.last(centerSegments).timestamp),
            algoVersion: this.store.algoVersion,
          };
        }

        // We need at least two indexes to run the interpolation.
        // If there's only one, make the closest one a fifth of a second away.
        if (foundIndexes.length === 1) {
          const foundIndexIndex = pqrstIndexes.indexOf(foundIndexes[0]);
          if (foundIndexIndex === 0) {
            pqrstIndexes[1] = foundIndexes[0] + sampleRate * 0.2;
          } else {
            pqrstIndexes[foundIndexIndex - 1] =
              foundIndexes[0] - sampleRate * 0.2;
          }
        }

        // Linear interpolate function.
        function lerp(a, b, f) {
          return a + f * (b - a);
        }
        pqrstIndexes.forEach((val, i) => {
          // Fill in missing values.
          if (isNull(val)) {
            // Find the closest valid values, and make the current value fit them.
            const closest = [];
            // Look for the closest two items moving outward on unit at a time.
            for (let j = 1; pqrstIndexes[i + j] || pqrstIndexes[i - j]; j++) {
              // eslint-disable-next-line
              [i - j, i + j].forEach((k) => {
                if (pqrstIndexes[k] && !isNull(pqrstIndexes[k])) {
                  closest.push({ index: k, val: pqrstIndexes[k] });
                }
              });
              // Once it's found two points to work with, we can interpolate the missing value.
              if (closest.length >= 2) {
                closest.splice(2, 1);
                const a = _.minBy(closest, "index");
                const b = _.maxBy(closest, "index");
                const range = Math.abs(a.index - b.index);
                const offset = i - a.index;
                const f = offset / range;

                pqrstIndexes[i] = lerp(a.val, b.val, f);
                break;
              }
            }
          }
        });

        // Remove the R index, because we don't put a marker there.
        pqrstIndexes.splice(2, 1);
        markers.forEach((marker, i) => {
          let timeIndex = pqrstIndexes[i];
          if (!isNull(timeIndex)) {
            marker.timestamp = centerSegments[0].timestamp.dateTime.plus({
              seconds: timeIndex / sampleRate,
            });
            marker.position.x = this.timeToPosition(marker.timestamp);
          }
        });
        this.store.markers = markers;
        this.calcDistances();
      })
      .catch(() => {
        this.store.snippetRawData = {
          defaultPQRSTIndex: [],
          sampleRate,
          waveData,
          waveLengthSeconds: centerSegments.length,
          waveStartTime: toLuxon(centerSegments[0].timestamp),
          waveEndTime: toLuxon(_.last(centerSegments).timestamp),
          algoVersion: this.store.algoVersion,
        };
      });
  }

  startDragMarker = (e) => {
    this.store.offsetX = 0;
    this.store.originalChartEndTime = this.store.chartEndTime;
    this.store.originalStripEndTime = this.store.stripEndTime;
    const event = e?.touches?.[0] || e;
    this.mouseDownEvent = {
      clientX: event.clientX,
      clientY: event.clientY,
    };
    this.draggingMarker = true;
  };
  handleDragMarker = (e, data, i) => {
    this.handleOverDrag(data);

    data.y = _.clamp(data.y, 0, this.refChart.offsetHeight - 40);
    this.store.markers[i].position = { x: data.x, y: data.y };
    data.x = _.clamp(data.x, 20, this.refChart.offsetWidth - 20);

    if (this.store.markers.length === 2) {
      this.store.markers.forEach((marker) => {
        marker.position.y = data.y;
      });
    }
    this.store.markers[i].timestamp = this.store.chartEndTime.minus({
      seconds: (this.refCanvas.width - data.x) / this.store.pixelsPerSecond,
    });
    this.calcDistances();
  };
  stopDragMarker = (e, data, i) => {
    this.draggingMarker = false;
    // Toggle Marker
    if (
      this.store.measurementMode &&
      e.clientX === this.mouseDownEvent.clientX &&
      e.clientY === this.mouseDownEvent.clientY
    ) {
      this.store.markers[i].muted = !this.store.markers[i].muted;
      this.store.markers[i] = { ...this.store.markers[i] };
    }
    this.calcDistances();
    this.store.offsetX = 0;
  };
  startDragBothCalipers(_e) {
    const e = _.get(_e, "touches[0]", _e);
    this.mouseDownEvent = e;
    let markerOffsets = this.store.markers.map((marker) => ({
      x: marker.position.x - e.clientX,
      y: marker.position.y - e.clientY,
    }));
    let mouseMove = (_e) => {
      const e = _.get(_e, "touches[0]", _e);
      this.store.markers.forEach((marker, i) => {
        this.handleDragMarker(
          e,
          {
            x: e.clientX + markerOffsets[i].x,
            y: e.clientY + markerOffsets[i].y,
          },
          i,
        );
      });
    };
    let mouseUp = () => {
      window.removeEventListener("mousemove", mouseMove);
      window.removeEventListener("mouseup", mouseUp);
      window.removeEventListener("touchmove", mouseMove);
      window.removeEventListener("touchend", mouseUp);
      this.calcDistances && this.calcDistances();
    };
    window.addEventListener("mousemove", mouseMove);
    window.addEventListener("mouseup", mouseUp);

    window.addEventListener("touchmove", mouseMove);
    window.addEventListener("touchend", mouseUp);
    window.addEventListener("touchcancel", mouseUp);
  }

  startTime(canvas = this.refCanvas) {
    const isStrip = canvas === this.refRhythmStripCanvas;
    const endTime = isStrip ? this.store.stripEndTime : this.store.chartEndTime;
    const pixelsPerSecond = isStrip
      ? this.store.minPixelsPerSecond
      : this.store.pixelsPerSecond;
    return toLuxon(endTime).minus({ seconds: canvas.width / pixelsPerSecond });
  }
  get centerTime() {
    return this.store.chartEndTime.minus({
      seconds: this.chartLengthSeconds / 2,
    });
  }
  calcDistances() {
    if (this.store.measurementMode) {
      this.store.PR = this.store.calcSnippetDistance(0, 1);
      this.store.QRS = this.store.calcSnippetDistance(1, 2);
      this.store.QT = this.store.calcSnippetDistance(1, 3);
      if (config.pm.qtc) {
        this.store.QTc = this.store.getQTc();
      }
    } else {
      const caliperDistance = Math.abs(
        this.store.calcSnippetDistance(0, 1),
      ).toFixed(2);
      this.store.caliperDistance = `${caliperDistance}s (${parseInt(
        60 / caliperDistance,
        10,
      )}/min)`;
    }

    this.checkPQRSTBoundaryBreach();
  }
  setChartSize({ onMount }) {
    const canvas = this.refCanvas;
    if (!canvas) return;

    const centerTime = toLuxon(this.centerTime);

    canvas.height = this.refChart.offsetHeight;
    canvas.width = this.refChart.offsetWidth;
    if (this.store.chartEndTime && !onMount) {
      this.store.chartEndTime = toLuxon(this.store.chartEndTime).plus({
        milliseconds: centerTime
          .diff(this.centerTime, "milliseconds")
          .as("milliseconds"),
      });
      this.syncBoundaryPositions();
      this.syncCaliperPositions();
    }
    this.store.chartWidth = canvas.width;

    this.renderCharts();
  }
  renderCharts() {
    const canvas = this.refCanvas;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    ctx.clearRect(0, 0, canvas.width, canvas.height, false);
    // center rhythm-strip
    this.centerRhythmStrip();

    const endTimeOffset = this.store.offsetX / this.store.pixelsPerSecond;

    if (this.store.originalChartEndTime && this.store.offsetX) {
      this.store.chartEndTime = toLuxon(this.store.originalChartEndTime).plus({
        seconds: endTimeOffset,
      });
    } else if (this.store.chartEndTime) {
      this.store.chartEndTime = toLuxon(this.store.chartEndTime).plus({
        seconds: endTimeOffset,
      });
    }
    this.renderTimestamp();

    if (this.store.config) {
      // Limit vertical scrolling to drawing area.
      ctx.fillStyle = this.paperMode ? "#fff" : "#000";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      this.store.offsetY = _.clamp(
        this.store.offsetY,
        Math.min(0, -canvas.height / 2),
        Math.max(
          0,
          (this.store.pixelsPerSecond / this.store.chartAspect) * 2 -
            canvas.height,
        ),
      );

      this.setStripSize();
      this.setStartTimes();
      this.renderGrid({
        ctx,
        linesPerSecond: 25,
      });
      this.renderGrid({
        ctx,
        linesPerSecond: 5,
        heavy: true,
      });
      this.renderWaves({
        canvas,
        pixelsPerSecond: this.store.pixelsPerSecond,
        offsetY: this.store.offsetY,
        chartEndTime: this.store.chartEndTime,
      });

      if (!this.store.enableSelectTime && !this.enableSelectTimeTimeout) {
        this.enableSelectTimeTimeout = setTimeout(() => {
          this.store.enableSelectTime = true;
        }, 200);
      }
    }
    this.renderRhythmStrip();
  }

  renderTimestamp() {
    if (this.refTimestamp) {
      this.refTimestamp.innerText = formatDateTime(
        toLuxon(this.store.chartEndTime).toISO(),
        "-",
      );
      this.refStripTimestamp.innerText = formatTime(
        toLuxon(this.store.stripEndTime).toISO(),
      );
    }
  }
  updateStripBuffer() {
    this.store.liveMode = false;
    if (!this.refRhythmStripCanvas) return;
    return this.store.updateMeasurementBuffers({
      pixelWidth: this.refRhythmStripCanvas.width,
      pixelsPerSecond: this.store.minPixelsPerSecond,
      chartEndTime: this.store.stripEndTime,
    });
  }

  renderWaves({
    canvas,
    pixelsPerSecond,
    offsetY,
    chartEndTime,
    snippetsWaveforms = this.store.toolsWaveforms,
  }) {
    if (!canvas || !chartEndTime) return;
    const ctx = canvas.getContext("2d");
    const chartLengthSeconds = canvas.width / pixelsPerSecond;
    const chartStartTime = chartEndTime.minus({ seconds: chartLengthSeconds });
    let slicedSegments = this.store.sliceWaveforms({
      chartStartTime,
      chartEndTime,
    });

    const chartHeight = pixelsPerSecond / this.store.chartAspect;
    const isStrip = canvas === this.refRhythmStripCanvas;

    snippetsWaveforms.forEach((group) => {
      if (!group) return;
      let level = group.key === snippetsWaveforms[0].key ? 1 : 2;
      let yScale = generateYScale({
        group,
        pixelsPerSecond: isStrip
          ? this.store.minPixelsPerSecond
          : this.store.pixelsPerSecond,
        yOffset: chartHeight * level - offsetY,
        chartHeight,
      });

      let lineColor = group.lineColor || "#0a0";
      if (this.paperMode) {
        lineColor = group.outer ? "#999" : "#000";
      }

      ctx.strokeStyle = lineColor;
      ctx.lineWidth = 2;
      ctx.beginPath();
      let firstPoint, lastRenderedSecond;

      slicedSegments
        .filter((segment, i) =>
          group.range
            ? toLuxon(
                _.get(
                  slicedSegments,
                  `[${i === slicedSegments.length ? i : i + 1}].timestamp`,
                ),
              ) >= toLuxon(group.range.start) &&
              toLuxon(segment.timestamp) <= toLuxon(group.range.end)
            : true,
        )
        .forEach((segment, i) => {
          let waveSegment = segment.waveData.find(
            (wave) => wave.key === group.key,
          );
          if (waveSegment && waveSegment.data) {
            if (!waveSegment.data.length) {
              firstPoint = false;
              return;
            }
            if (
              toLuxon(segment.timestamp)
                .diff(toLuxon(lastRenderedSecond), "seconds")
                .as("seconds") > 1
            ) {
              firstPoint = false; // leave a gap for non-consecutive segments.
            }
            let pointsPerSecond = waveSegment.data.length;
            let frameTimeToEnd_ms = toLuxon(segment.timestamp)
              .diff(toLuxon(chartEndTime))
              .as("milliseconds");
            let rangeStartMS =
              group.range && i === 0
                ? toLuxon(group.range.start).milliseconds
                : 0;
            yScale = yScale.update(waveSegment);

            waveSegment.data.forEach((point, pointIndex) => {
              // drowPoint()
              if (point === null) {
                firstPoint = false;
                return;
              }
              let fractionOfSecond = pointIndex / pointsPerSecond;

              if (pointIndex / pointsPerSecond < rangeStartMS / 1000) {
                return;
              }

              let x =
                (frameTimeToEnd_ms / 1000 + fractionOfSecond) *
                  pixelsPerSecond +
                ctx.canvas.width;
              if (x < 0) {
                return;
              }
              if (!firstPoint) {
                firstPoint = true;
                ctx.moveTo(x, yScale(point));
              } else {
                ctx.lineTo(x, yScale(point));
              }
            });
            lastRenderedSecond = toLuxon(segment.timestamp);
          } else {
            firstPoint = false;
            return;
          }
        });
      ctx.stroke();
    });
    if (this.store.marchout) {
      this.renderMarchout({ canvas });
    }
    this.renderBoundaries({ canvas, chartEndTime });
  }

  renderMarchout({ canvas }) {
    if (!this.store.caliperMode) return;

    let caliperPositions = this.store.markers.map((marker) => {
      let isStrip = canvas === this.refRhythmStripCanvas;
      return this.timeToPosition(marker.timestamp, isStrip);
    });
    let distance = Math.abs(caliperPositions[1] - caliperPositions[0]);
    let offset = caliperPositions[0] % distance;
    if (!distance || distance < 2 || !offset) return;

    this.marchoutColor = "#1CACDD";
    let ctx = canvas.getContext("2d");
    ctx.strokeStyle = this.marchoutColor;
    ctx.lineWidth = 1;
    ctx.beginPath();

    let i = 0;
    for (let x = offset; x <= canvas.width; x = x + distance) {
      ctx.moveTo(x, 0);
      ctx.lineTo(x, canvas.height);
      if (i++ > 1000) break;
    }
    ctx.stroke();
  }

  syncBoundaryTimes() {
    if (this.store.measurementMode && !this.draggingBoundary) return;

    this.store.boundaries?.forEach(this.syncBoundaryTime);
  }
  syncBoundaryTime = (boundary) => {
    if (
      this.refRhythmStripCanvas &&
      this.startTime(this.refRhythmStripCanvas)
    ) {
      boundary.timestamp = this.startTime(this.refRhythmStripCanvas)
        .plus({
          seconds: boundary.stripPosition.x / this.store.minPixelsPerSecond,
        })
        .toISO();
    }
  };
  syncBoundaryPositions({ skipIndex = null } = {}) {
    if (this.draggingBoundary && skipIndex === null) {
      return;
    }

    if (this.store.measurementMode && this.store.boundaries) {
      this.store.boundaries.forEach((boundary, i) => {
        if (i !== skipIndex) {
          boundary.stripPosition.x = this.timeToPosition(
            boundary.timestamp,
            true,
          );
        }
      });
    }
  }

  renderBoundaries({ canvas }) {
    if (!this.store.boundaries) {
      return;
    }
    if (!this.draggingBoundary) this.syncBoundaryTimes();

    const ctx = canvas.getContext("2d");
    ctx.fillStyle = this.paperMode
      ? "rgba(0,0,0,0.3)"
      : "rgba(255,255,255,0.3)";

    const isStrip = canvas === this.refRhythmStripCanvas;

    const startPosition = this.timeToPosition(
      this.store.boundaries[0].timestamp,
      isStrip,
    );
    ctx.fillRect(0, 0, startPosition, canvas.height);
    const endBoundPosition = this.timeToPosition(
      this.store.boundaries[1].timestamp,
      isStrip,
    );
    ctx.fillRect(
      endBoundPosition,
      0,
      canvas.width - endBoundPosition,
      canvas.height,
    );

    if (
      Math.abs(this.store.boundaries[0].x - startPosition) > 1 ||
      Math.abs(this.store.boundaries[0].x - endBoundPosition) > 1
    ) {
      requestAnimationFrame(
        () =>
          (this.store.boundaries = [
            { x: startPosition, y: 0 },
            { x: endBoundPosition, y: 0 },
          ]),
      );
    }
  }

  setStripSize() {
    this.refRhythmStripCanvas.height = rhythmStripHeight;
    this.refRhythmStripCanvas.width = this.refChart.offsetWidth;
  }

  setStartTimes() {
    const selectionStartTime = this.store.chartEndTime
      ? this.store.chartEndTime.minus({
          seconds: this.refCanvas.width / this.store.pixelsPerSecond,
        })
      : DateTime.local().minus({
          seconds: this.refCanvas.width / this.store.pixelsPerSecond,
        });

    const stripStartTime = this.store.stripEndTime
      ? this.store.stripEndTime.minus({
          seconds:
            this.refRhythmStripCanvas.width / this.store.minPixelsPerSecond,
        })
      : DateTime.local().minus({
          seconds:
            this.refRhythmStripCanvas.width / this.store.minPixelsPerSecond,
        });

    this.store.stripStartTime = stripStartTime;
    this.store.chartStartTime = selectionStartTime;
  }

  renderRhythmStrip() {
    const gray = "#595959";
    const pixelsPerSecond = this.store.minPixelsPerSecond;
    const chartHeight = getChartHeight({ pixelsPerSecond });
    const offsetY = chartHeight - this.refRhythmStripCanvas.height / 2;
    const splitWaveforms = this.store.toolsWaveforms.reduce((acc, group) => {
      return [
        ...acc,
        {
          ...group,
          lineColor: gray,
          range: {
            start: this.store.stripStartTime,
            end: this.store.chartStartTime,
          },
          outer: true,
        },
        {
          ...group,
          range: {
            start: this.store.chartStartTime,
            end: this.store.chartEndTime,
          },
        },
        {
          ...group,
          lineColor: gray,
          range: {
            start: this.store.chartEndTime,
            end: this.store.stripEndTime,
          },
          outer: true,
        },
      ];
    }, []);

    this.renderGrid({
      ctx: this.refRhythmStripCanvas.getContext("2d"),
      linesPerSecond: 5,
    });
    this.renderWaves({
      canvas: this.refRhythmStripCanvas,
      pixelsPerSecond,
      offsetY,
      chartEndTime: this.store.stripEndTime,
      snippetsWaveforms: splitWaveforms,
    });
  }

  renderGrid({ ctx, linesPerSecond, heavy }) {
    // render vertical lines
    const isZoomedCanvas = ctx.canvas === this.refCanvas;
    const pixelsPerSecond = isZoomedCanvas
      ? this.store.pixelsPerSecond
      : this.store.minPixelsPerSecond;
    const lineSpacing = pixelsPerSecond / linesPerSecond;
    if (lineSpacing < 6) return;

    const lineColor = this.paperMode ? "#FE6A52" : "#666";
    const alpha = this.paperMode ? (heavy ? 1 : 0.3) : heavy ? 1 : 0.5;

    ctx.strokeStyle = tinycolor(lineColor).setAlpha(alpha).toRgbString();
    ctx.lineWidth = 1;

    ctx.beginPath();

    const chartStartTime = isZoomedCanvas
      ? toLuxon(this.store.chartStartTime)
      : toLuxon(this.store.stripStartTime);
    const offsetMilliseconds =
      (1000 - chartStartTime.millisecond) % (1000 / linesPerSecond);
    const offsetSeconds = offsetMilliseconds / 1000;
    const offsetPixels = offsetSeconds * pixelsPerSecond;

    for (
      let offsetX = offsetPixels;
      offsetX <= ctx.canvas.width;
      offsetX += lineSpacing
    ) {
      ctx.moveTo(offsetX, 0);
      ctx.lineTo(offsetX, ctx.canvas.height);
    }
    ctx.strokeStyle = tinycolor(lineColor).setAlpha(alpha).toRgbString();
    ctx.lineWidth = 1;

    // render horizontal Lines
    const offsetY = isZoomedCanvas ? this.store.offsetY : 0;
    for (
      let y = -offsetY % lineSpacing;
      y <= ctx.canvas.height;
      y += lineSpacing
    ) {
      if (y > 0) {
        ctx.moveTo(0, y);
        ctx.lineTo(ctx.canvas.width, y);
      }
    }

    ctx.stroke();
  }
  /* istanbul ignore next */
  updateZoom() {
    this.store.offsetX = 0;
    let originalPixelsPerSecond = this.store.pixelsPerSecond;
    let chartLengthSeconds =
      this.store.scrubberWidth / this.store.minPixelsPerSecond;
    this.store.pixelsPerSecond = this.store.stripWidth / chartLengthSeconds;
    this.store.offsetY *= this.store.pixelsPerSecond / originalPixelsPerSecond;
  }

  setHandlePos(data, handleId) {
    if (!this.store.boundaries) return;
    const boundaryIndex = handleId === "start" ? 0 : 1;
    this.handleOverDrag(data);
    this.syncBoundaryPositions({ skipIndex: boundaryIndex });
    this.store.boundaries[boundaryIndex].stripPosition = { x: data.x, y: 0 };
    this.checkPQRSTBoundaryBreach();
  }

  /**
   * Loops through boundaries and markers to determine if any marker is out of bounds.
   * This sets a flag in MonitorStore to indicate that PQRST is out of bounds.
   * In addition, this flag is used to throw an error modal to the user,
   * prompting them to move their markers back within bounds.
   */
  checkPQRSTBoundaryBreach() {
    if (!this.store.boundaries) return false;
    const boundaryThresholds = this.store.boundaries.map((boundary) =>
      this.timeToPosition(boundary.timestamp),
    );

    this.store.isPQRSTBoundaryBreach = this.store.markers
      .map((marker) => {
        return (
          this.timeToPosition(marker.timestamp) < boundaryThresholds[0] ||
          this.timeToPosition(marker.timestamp) > boundaryThresholds[1]
        );
      })
      .some(Boolean);
  }

  handleDragBoundary(e, data) {
    if (!this.store.boundaries) return;
    const handleId = data.node.getAttribute("data-id");
    let boundaryIndex = null;
    switch (handleId) {
      case "start":
        this.setHandlePos(data, handleId);
        boundaryIndex = 0;
        break;
      case "end":
        this.setHandlePos(data, handleId);
        boundaryIndex = 1;
        break;
      default:
        console.error("unexpected data-id for drag handle.");
        this.store.boundaries = [...this.store.boundaries];
    }
    this.syncBoundaryTime(this.store.boundaries[boundaryIndex]);

    this.customLengthSeconds =
      (this.store.boundaries[1].stripPosition.x -
        this.store.boundaries[0].stripPosition.x) /
      this.store.minPixelsPerSecond;
    this.store.seconds = {
      value: this.customLengthSeconds,
      label: `${_.round(this.customLengthSeconds, 1)} seconds`,
    };
  }
  startDragBoundary = () => {
    if (!this.store.boundaries) return;
    this.draggingBoundary = true;
  };
  endDragBoundary = () => {
    if (!this.store.boundaries) return;
    this.draggingBoundary = false;
    this.overDragOffset = 0;
    this.continueOverscroll = () => {};
    this.syncBoundaryPositions();
  };

  timeToPosition(timestamp, isStrip) {
    if (!this.refCanvas) {
      return;
    }

    const ts = toLuxon(timestamp);

    let resolution = this.store.pixelsPerSecond;
    let endTime = toLuxon(this.store.chartEndTime);
    if (isStrip) {
      resolution = this.store.minPixelsPerSecond;
      endTime = this.store.stripEndTime;
    }

    const canvasWidth = this.refCanvas.width;
    const timestampDiffFromEndTime = endTime
      ? ts.diff(endTime, "seconds").seconds
      : 0;
    const timeToPos = canvasWidth + resolution * timestampDiffFromEndTime;
    return timeToPos;
  }

  secondsChange(option = this.lengthOptions[0]) {
    const defaultOption = {
      value: 120,
      label: `120 seconds`,
    };

    this.store.seconds = option ? option : defaultOption;

    let boundaries = [
      toLuxon(this.centerTime).minus({ seconds: this.store.seconds.value / 2 }),
      toLuxon(this.centerTime).plus({ seconds: this.store.seconds.value / 2 }),
    ];
    this.store.boundaries = boundaries.map((boundary) => ({
      timestamp: boundary,
      stripPosition: { x: this.timeToPosition(boundary, true), y: 0 },
    }));
  }
  defaultSeconds = () => {
    if (this.store.measurementMode) return;
    if (this.store.chartEndTime) {
      this.centerRhythmStrip();
      this.secondsChange(this.store.seconds);
    } else {
      requestAnimationFrame(this.defaultSeconds);
    }
  };
  async defaultLeads() {
    await this.props.store.prefsPromise;
    await when(() => this.store.config);
    const single =
      Number(
        this.props.store.prefs?.userPreferences?.server?.pm
          ?.snippetsDefaultLeadsSelection,
      ) === 1;
    this.store.getWaveGroups({ single });
  }

  boundaries(stripWidth) {
    if (!this.store.boundaries) return null;
    const startRightBound =
      (this.store.boundaries[1].stripPosition.x ?? stripWidth / 2) - 10;
    const endLeftBound =
      (this.store.boundaries[0].stripPosition.x ?? stripWidth / 2) + 10;
    return [
      <Draggable
        axis="x"
        key="start"
        onDrag={this.handleDragBoundary}
        bounds={{ left: 0, right: startRightBound }}
        position={this.store.boundaries[0].stripPosition}
        onStart={this.startDragBoundary}
        onStop={this.endDragBoundary}
      >
        <div className="drag-handle start" data-id="start"></div>
      </Draggable>,

      <Draggable
        axis="x"
        key="end"
        onDrag={this.handleDragBoundary}
        defaultPosition={{
          x: this.props.scrubberWidth || stripWidth / 4,
          y: 0,
        }}
        bounds={{ left: endLeftBound, right: stripWidth }}
        position={this.store.boundaries[1].stripPosition}
        onStart={this.startDragBoundary}
        onStop={this.endDragBoundary}
      >
        <div className="drag-handle end" data-id="end"></div>
      </Draggable>,
    ];
  }
  createStatement() {
    // Pass store to CreateStatement through global store.
    let errorMsg = null;
    if (this.store.boundaries?.length !== 2)
      errorMsg = "Please select a time range.";
    else if (this.store.isPQRSTBoundaryBreach)
      errorMsg = "Calipers outside boundaries of snippet";

    if (errorMsg) {
      return Swal.fire({
        title: "Cannot Create Snippet",
        icon: "error",
        html: errorMsg,
        showConfirmButton: false,
      });
    }
    // TODO: we should be sending something else instead of the whole store.
    // This is risking memory leaks.
    this.store.snippetStartTime = DateTime.fromISO(
      this.store.boundaries[0].timestamp,
    );
    this.store.snippetEndTime = DateTime.fromISO(
      this.store.boundaries[1].timestamp,
    );
    clearInterval(this.updateStripInterval);
    this.props.history.push(`/patient/${this.asmpid}/monitor/create-statement`);
  }

  centerRhythmStrip() {
    if (!this.refCanvas) return;
    let stripWidthSeconds =
      this.store.stripWidth / this.store.minPixelsPerSecond;
    let chartLengthSeconds = this.refCanvas.width / this.store.pixelsPerSecond;
    let extraTimeSeconds = stripWidthSeconds - chartLengthSeconds;
    this.store.stripEndTime =
      this.store.chartEndTime?.isValid &&
      this.store.chartEndTime.plus({
        seconds: extraTimeSeconds / 2,
      });
  }

  selectTime(e) {
    e && e.preventDefault();
    this.showHistorySelector(e)
      .then((time) => {
        this.updateTime(time);
      })
      .catch(console.error);
  }

  selectTimePath(endTime = this.store.chartEndTime) {
    return `/patient/${this.asmpid}/monitor/${
      this.isSnippetRoute ? "snippet" : "tools"
    }/${endTime}`;
  }
  updateTime(endTime) {
    if (!endTime) return;
    endTime = toLuxon(endTime);
    this.store.resetMeasurements();

    this.props.history.push(this.selectTimePath(endTime));

    if (!this.refCanvas) {
      return;
    }

    const startTime = endTime.minus({
      seconds: this.refCanvas.clientWidth / this.store.pixelsPerSecond,
    });

    this.store
      .fetchMonitorMeasurements(
        {
          endtime: endTime.toISO(),
          starttime: startTime.toISO(),
        },
        true,
      )
      .then(() => {
        this.store.chartEndTime = endTime;
        this.store.originalChartEndTime = this.store.chartEndTime;
        // reset boundaries.
        if (this.store.measurementMode) {
          this.syncBoundaryPositions();
        }
        this.renderCharts();
      });
  }
}

export default inject("store")(observer(Tools));

export function handleOverDrag(e) {
  // Scrolls the chart when you drag a marker over the side.
  if (this.store.caliperMode) return;
  if (this.store.measurementMode) {
    let offsetX;
    if (e.x > this.refCanvas.width - 20) {
      offsetX = e.x - (this.refCanvas.width - 20);
    } else if (e.x < 40) {
      offsetX = e.x - 40;
    } else {
      offsetX = 0;
    }
    this.overDragOffset = offsetX;
    if (this.overDragOffset && !this.continueOverscroll) {
      this.continueOverscroll = () => {
        if (!(this.draggingBoundary || this.draggingMarker)) return;
        this.store.offsetX += this.overDragOffset;
        this.renderCharts();
        this.syncBoundaryPositions();
        requestAnimationFrame(() => this.continueOverscroll?.());
      };
      this.continueOverscroll();
    } else {
      this.continueOverscroll = undefined;
    }
  }
}
